/**
 * Web ürün açıklaması ve SEO içeriği üretimi (ürün ekleme + liste eksik alan doldurma).
 */

const SEO_TITLE_MAX = 60;
const SEO_TITLE_SUFFIX = ' | UKBA GOLD';
const SEO_DESC_MIN = 140;
const SEO_DESC_MAX = 160;
const SEO_KEYWORD_MAX = 24;

/** İsim + türe göre deterministik varyasyon (random değil). */
function variantBucket(seed, modulo) {
  let h = 0;
  const s = String(seed || '');
  for (let i = 0; i < s.length; i += 1) {
    h = (h * 31 + s.charCodeAt(i)) >>> 0;
  }
  return modulo > 0 ? h % modulo : 0;
}

function escapeHtml(text) {
  return String(text || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export function slugifyTR(text) {
  const cleaned = String(text || '')
    .toLocaleLowerCase('tr-TR')
    .replace(/ı/g, 'i')
    .replace(/ğ/g, 'g')
    .replace(/ü/g, 'u')
    .replace(/ş/g, 's')
    .replace(/ö/g, 'o')
    // ç→cc: Latin "c" ile aynı segmente düşüp İkas'ta slug çakışmasını önler (C harfi ≠ Ç harfi)
    .replace(/ç/g, 'cc')
    .replace(/[^a-z0-9\s-]/g, ' ')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '');
  return cleaned;
}

export function uniquePush(arr, item) {
  if (!item) return;
  const key = item.toLocaleLowerCase('tr-TR');
  if (!arr.some((x) => x.toLocaleLowerCase('tr-TR') === key)) arr.push(item);
}

export function toTitleCaseTR(text) {
  return String(text || '')
    .toLocaleLowerCase('tr-TR')
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toLocaleUpperCase('tr-TR') + w.slice(1))
    .join(' ');
}

/** bilezik | kolye | kupe | yuzuk | diger */
function resolveProductKind(isim, urunTipi, kategori) {
  const haystack = `${isim} ${urunTipi} ${kategori}`.toLocaleLowerCase('tr-TR');
  if (/bilezik|bileklik/i.test(haystack)) return 'bilezik';
  if (/kolye|kolye ucu|gümüş zincir|zincir/i.test(haystack)) return 'kolye';
  if (/küpe|kupe/i.test(haystack)) return 'kupe';
  if (/yüzük|yuzuk|alyans/i.test(haystack)) return 'yuzuk';
  return 'diger';
}

function buildSlug({ isim, zincir }) {
  const parts = [isim, zincir].filter((x) => x != null && String(x).trim() !== '').join(' ');
  return slugifyTR(parts);
}

function typeWordFromKind(kind) {
  if (kind === 'bilezik') return 'bilezik';
  if (kind === 'kolye') return 'kolye';
  if (kind === 'kupe') return 'küpe';
  if (kind === 'yuzuk') return 'yüzük';
  return 'takı';
}

/** Ürün adı / tip / kategoride 22 ayar bilezik eşleşmesi */
function isTwentyTwoAyarBilezik(ctx) {
  const { isim, urunTipi, kategori, kind } = ctx;
  if (kind !== 'bilezik') return false;
  const hay = `${isim} ${urunTipi} ${kategori}`.toLocaleLowerCase('tr-TR');
  if (/22\s*ayar\s*bilezik/i.test(hay)) return true;
  if (/\b22\s*ayar\b/i.test(hay) && /bilezik|bileklik/i.test(hay)) return true;
  return false;
}

function pickVariant(arr, seed, kind) {
  const idx = variantBucket(`${seed}|${kind}`, arr.length);
  return arr[idx];
}

function specRow(label, value) {
  if (value == null || String(value).trim() === '') return '';
  return `<p><strong>${escapeHtml(label)}:</strong> ${escapeHtml(String(value).trim())}</p>`;
}

function isChildOrBabyProduct(isim, kategori) {
  const h = `${isim} ${kategori}`.toLocaleLowerCase('tr-TR');
  return /bebek|çocuk/i.test(h);
}

function isStonelessTas(tas) {
  const raw = String(tas || '').trim();
  if (!raw) return false;
  const norm = raw.toLocaleUpperCase('tr-TR').replace(/\s+/g, '');
  return norm === 'TAŞSIZ' || norm === 'TASSIZ';
}

function tasNaturalPhrase(tas) {
  if (!tas || !String(tas).trim()) return '';
  if (isStonelessTas(tas)) return 'Taşsız yapısıyla sade ve kullanışlı bir görünüm sunar.';
  return `${toTitleCaseTR(tas)} işlemesi tasarıma özgün bir dokunuş ekler.`;
}

function buildSpecsHtml(ctx) {
  const { ayar, milyemK, gramText, zincir, ebat, ebatLabel, model, boyut, tas, renk } = ctx;
  const rows = [];
  const ayarDisplay = milyemK ? `${ayar} (${milyemK})` : ayar;
  rows.push(specRow('Ayar', ayarDisplay));
  if (gramText) rows.push(specRow('Ağırlık', `${gramText} gr (±%10 üretim toleransı)`));
  if (zincir && String(zincir).trim()) rows.push(specRow('Zincir uzunluğu', zincir.trim()));
  if (ebat && String(ebat).trim()) rows.push(specRow(ebatLabel, ebat.trim()));
  if (model && String(model).trim()) rows.push(specRow('Model', model.trim()));
  if (boyut && String(boyut).trim()) rows.push(specRow('Boyut', boyut.trim()));
  if (tas && String(tas).trim()) {
    if (isStonelessTas(tas)) rows.push(specRow('Taş', 'Taşsız'));
    else rows.push(specRow('Taş / işlem', tas.trim()));
  }
  if (renk && String(renk).trim()) rows.push(specRow('Renk', renk.trim()));
  return rows.join('');
}

function buildIntroParagraph(ctx) {
  const { kind } = ctx;
  const seed = `${ctx.isim}|${ctx.kind}`;
  const childBaby = ctx.isChildBaby && kind === 'kupe';

  if (ctx.isTwentyTwoAyarBilezik) {
    const pool = [
      'İşçiliksiz bilezik yapısıyla öne çıkan bu model, bilekte dengeli ve şık bir duruş sunar; günlük kullanımda stilinizi zarif bir şekilde tamamlar.',
      '22 ayar işçiliksiz bilezik olarak hazırlanan bu parça, bileğinizde sade ama güçlü bir UKBA GOLD ifadesi oluşturur.',
      'Yatırım ve günlük şıklığı birlikte düşünenler için işçiliksiz bilezik formuyla dengeli ve güven veren bir seçenektir.'
    ];
    return pickVariant(pool, `${seed}|i22`, 'bilezik');
  }

  const bilezik = [
    'Bilekte dengeli ve şık bir duruş sunan bu bilezik, günlük kullanımda stilinizi zarif bir şekilde tamamlar.',
    'Bileğinize uyumlu hatlarıyla öne çıkan bu bilezik, günlük kombinlerinize sofistike bir tamamlayıcı ekler.',
    'Sade çizgisiyle dikkat dağıtmadan şıklığınızı tamamlayan bu bilezik, hem işte hem davette kullanıma uygundur.'
  ];
  const kolye = [
    'Minimal formu ve dengeli yapısıyla bu kolye, günlük stilinize sade ama güçlü bir karakter kazandırır.',
    'Boyun hattınızı nazikçe vurgulayan bu kolye, özel günlere ve günlük şıklığa aynı rahatlıkla uyum sağlar.',
    'İnce profili sayesinde tek başına güçlü durur; katmanlı kullanımlarda da uyumlu kalır.'
  ];
  const kupeNormal = [
    'Hafif yapısı ve sade görünümüyle bu küpe, günlük kullanımda konforlu ve şık bir tercih sunar.',
    'Günlük stilinize zarif bir dokunuş ekleyen bu küpe, özel günlere de aynı rahatlıkla eşlik eder.',
    'Sade hatlarıyla yüzünüzü öne çıkaran bu küpe, hediye olarak da anlamlı bir seçim olur.'
  ];
  const kupeChild = [
    'Yuvarlak hatları ve sade görünümüyle çocuk ve bebek kullanımına uygun, güven veren bir hediye alternatifidir.',
    'Hafif yapısıyla küçük yaşta takı alışkanlığını destekleyen bu küpe, özel davetler için de uygundur.',
    'Doğum ve çocuk kutlamaları için düşünülmüş, kullanışlı bir UKBA GOLD hediye seçeneğidir.'
  ];
  const yuzuk = [
    'Elde zarif ve dikkat çekici bir duruş sunan bu yüzük, stilinize güçlü bir tamamlayıcı etki katar.',
    'Özel anları kutlamak veya günlük imzanızı güçlendirmek için üretilen bu yüzük, hediye olarak da öne çıkar.',
    'Nişan ve kutlamalardan günlük şıklığa uzanan kullanım alanı sunan dengeli bir yüzük modelidir.'
  ];
  const diger = [
    'Sade hatları ve özenli işçiliğiyle koleksiyonunuza güçlü bir UKBA GOLD parçası eklemenizi sağlar.',
    'Abartısız tasarımı sayesinde çoklu kombinlere kolayca uyum sağlayan seçkin bir üründür.',
    'Günlük kullanımdan özel günlere uzanan kullanım alanı sunar.'
  ];

  if (kind === 'bilezik') return pickVariant(bilezik, seed, kind);
  if (kind === 'kolye') return pickVariant(kolye, seed, kind);
  if (kind === 'kupe') return childBaby ? pickVariant(kupeChild, seed, kind) : pickVariant(kupeNormal, seed, kind);
  if (kind === 'yuzuk') return pickVariant(yuzuk, seed, kind);
  return pickVariant(diger, seed, kind);
}

function usageSentenceForKind(kind, seed) {
  if (kind === 'bilezik') {
    return pickVariant(
      [
        'Günlük kullanımda şık kalır; özel günlere de aynı parçayla uyumludur.',
        'İnce bileziklerle birlikte kullanıma uygundur.',
        'Tek başına takıldığında dengeli ve göze hoş bir tamamlayıcıdır.'
      ],
      `${seed}|usk`,
      kind
    );
  }
  if (kind === 'kolye') {
    return pickVariant(
      [
        'Günlük ve davet stilinize eşlik eder; katmanlı kullanımlarda uyumlu kalır.',
        'Sade kıyafetlerle birlikte zarif bir vurgu oluşturur.',
        'Boyun hattında rahat hissedilir; uzun süre takılmaya uygundur.'
      ],
      `${seed}|usk`,
      kind
    );
  }
  if (kind === 'kupe') {
    return pickVariant(
      [
        'İşte ve özel bir davette güvenle kullanılabilir.',
        'Hafif yapısıyla gün boyu konfor sunar.',
        'Hediye olarak paketlendiğinde anlamlı bir seçim olur.'
      ],
      `${seed}|usk`,
      kind
    );
  }
  if (kind === 'yuzuk') {
    return pickVariant(
      [
        'Nişan ve kutlama gibi özel anlarda öne çıkar; günlük kullanımda da şıklığınızı tamamlar.',
        'Tek yüzük olarak güçlü durur; uyumlu modellerle eşleştirilebilir.',
        'Elde dengeli ve konforlu bir kullanım sunar.'
      ],
      `${seed}|usk`,
      kind
    );
  }
  return pickVariant(
    [
      'Günlük kullanımdan özel günlere kadar geniş bir kullanım alanı sunar.',
      'Benzer parçalarla birlikte uyumlu setler oluşturmanıza yardımcı olur.',
      'Hediye olarak güven veren bir UKBA GOLD seçimidir.'
    ],
    `${seed}|usk`,
    kind
  );
}

function buildTechnicalParagraph(ctx) {
  const { kind, gramText, ebat, zincir, tas, seed } = ctx;
  const parts = [];

  if (gramText) {
    parts.push(`${gramText} gram ağırlığıyla hafif ve konforlu bir kullanım sunar.`);
  }
  if (ebat && String(ebat).trim()) {
    parts.push(`${String(ebat).trim()} ebatıyla sade ve dengeli bir görünüm oluşturur.`);
  }
  if (zincir && String(zincir).trim() && (kind === 'kolye' || kind === 'diger')) {
    parts.push(`${String(zincir).trim()} zincir uzunluğuyla boyun hattında dengeli bir duruş sağlar.`);
  }

  parts.push(usageSentenceForKind(kind, seed));

  const tasExtra = tasNaturalPhrase(tas);
  if (tasExtra) parts.push(tasExtra);

  return parts.join(' ');
}

function buildValueParagraph(ctx) {
  const { ayar, seed, kind } = ctx;
  if (ctx.isTwentyTwoAyarBilezik) {
    const pool = [
      'İşçiliksiz bilezik yapısı sayesinde yatırım ve birikim amacıyla tercih edilebilecek güçlü bir altın modelidir.',
      'Dayanıklı yapısı ve değerini koruyan altın formuyla uzun yıllar kullanabileceğiniz bu seçenek, birikim düşünenler için de uygundur.',
      '22 ayar işçiliksiz bilezik olarak hem şıklığı hem uzun vadeli değer algısını bir arada sunar.'
    ];
    return pickVariant(pool, `${seed}|val22`, kind);
  }
  const pool = [
    'Dayanıklı yapısı ve değerini koruyan altın formuyla uzun yıllar kullanabileceğiniz bu model, hem kendiniz hem de sevdikleriniz için güçlü bir hediye alternatifidir.',
    `${ayar} altının sunduğu kalıcılık ve UKBA GOLD güvencesiyle hem şık bir tercih hem anlamlı bir hediye olabilir.`,
    'Özenle üretilmiş bu model, günlük şıklığınızı güçlendirirken uzun ömürlü kullanım vaat eder.'
  ];
  return pickVariant(pool, `${seed}|val`, kind);
}

function ayarGoldBulletLabel(ayar) {
  const m = String(ayar || '').match(/(\d{1,2})\s*Ayar/i);
  if (m) return `${m[1]} Ayar altın yapısı`;
  return 'Kaliteli altın yapısı';
}

function buildFeatureBullets(ctx) {
  const { kind, isChildBaby, isTwentyTwoAyarBilezik } = ctx;
  const bullets = [];

  bullets.push('Günlük kullanıma uygun hafif yapı');
  bullets.push(ayarGoldBulletLabel(ctx.ayar));
  bullets.push('Zamansız tasarım çizgisi');
  bullets.push('Hediye alternatifi olarak ideal');
  bullets.push('Şık ve modern görünüm');

  if (isTwentyTwoAyarBilezik) {
    bullets.push('İşçiliksiz bilezik formu');
    bullets.push('Yatırım ve birikim için uygun yapı');
  }

  if (kind === 'kolye' || kind === 'kupe' || kind === 'yuzuk' || kind === 'diger') {
    bullets.push('Katmanlı kombinlere uyumlu tasarım');
  }

  if (isChildBaby && kind === 'kupe') {
    bullets.push('Çocuk ve bebek kullanımı için ideal');
  }

  const seen = new Set();
  const out = [];
  for (const b of bullets) {
    const k = b.toLocaleLowerCase('tr-TR');
    if (!seen.has(k)) {
      seen.add(k);
      out.push(b);
    }
  }
  return out.slice(0, 7);
}

function buildClosingParagraph(ctx) {
  const { seed, kind } = ctx;
  const pool = [
    'UKBA GOLD güvencesiyle güvenle sipariş verebilirsiniz.',
    'Detayları inceleyin; koleksiyonunuza eklemek için hazırız.',
    'UKBA GOLD ile işçilik ve ayar güvencesi tek çatı altında sunulur.'
  ];
  return pickVariant(pool, `${seed}|close`, kind);
}

function buildProductionDeliveryHtml() {
  return [
    '<p><strong>Üretim ve teslimat süreci</strong></p>',
    '<ul>',
    '<li>Bilezik atölyeden çıkma süresi <strong>3-4 iş günü</strong> sürebilir.</li>',
    '<li>Kargoya verilme süresi <strong>4-5 iş günü</strong> içerisinde tamamlanır.</li>',
    '</ul>'
  ].join('');
}

function finalizeSeoDescription(text) {
  let t = String(text).replace(/\s+/g, ' ').trim();
  if (t.length > SEO_DESC_MAX) {
    let cut = t.slice(0, SEO_DESC_MAX - 1);
    const ls = cut.lastIndexOf(' ');
    if (ls >= SEO_DESC_MIN - 40) cut = cut.slice(0, ls);
    t = `${cut.trim()}.`;
    if (t.length > SEO_DESC_MAX) t = t.slice(0, SEO_DESC_MAX - 3).trim() + '...';
  }
  const pads = [
    ' Güvenilir teslimat ve özenli paketleme.',
    ' Net ürün bilgisiyle güvenle seçim yapın.',
    ' UKBA GOLD ile online güvenli alışveriş.'
  ];
  let i = 0;
  while (t.length < SEO_DESC_MIN && i < pads.length) {
    const next = `${t}${pads[i]}`.replace(/\s+/g, ' ').trim();
    if (next.length <= SEO_DESC_MAX) t = next;
    i += 1;
  }
  if (t.length < SEO_DESC_MIN) {
    t = `${t} Hediye ve kişisel kullanım için uygun modelleri keşfedin.`.replace(/\s+/g, ' ').trim();
  }
  if (t.length > SEO_DESC_MAX) {
    let cut = t.slice(0, SEO_DESC_MAX - 1);
    const ls = cut.lastIndexOf(' ');
    if (ls >= SEO_DESC_MIN - 30) cut = cut.slice(0, ls);
    t = `${cut.trim()}.`;
    if (t.length > SEO_DESC_MAX) t = t.slice(0, SEO_DESC_MAX - 3).trim() + '...';
  }
  return t;
}

/**
 * Ürün adı yalnızca üstte bir kez; gövde paragraflarında tekrar edilmez.
 */
function buildDescription(ctx) {
  const { isim, kategori } = ctx;

  const seed = `${ctx.isim}|${ctx.kind}`;
  const is22Bilezik = isTwentyTwoAyarBilezik(ctx);

  const enriched = {
    ...ctx,
    seed,
    isChildBaby: isChildOrBabyProduct(ctx.isim, kategori),
    isTwentyTwoAyarBilezik: is22Bilezik
  };

  const parca1 = buildIntroParagraph(enriched);
  const parca2 = buildTechnicalParagraph(enriched);
  const parca3 = buildValueParagraph(enriched);
  const productionBlock = is22Bilezik ? buildProductionDeliveryHtml() : '';
  const specBlock = buildSpecsHtml(enriched);
  const highlights = buildFeatureBullets(enriched);
  const highlightsHtml = `<ul>${highlights.map((t) => `<li>${escapeHtml(t)}</li>`).join('')}</ul>`;

  const shipping = [
    'Sipariş özenle hediye paketi yapılarak gönderilir',
    'Siparişler fatura ve sertifika ile birlikte gönderilir',
    'Ürünlerde Ayar Garantisi mevcuttur'
  ];
  const shippingHtml = `<ul>${shipping.map((t) => `<li>${escapeHtml(t)}</li>`).join('')}</ul>`;

  const closing = buildClosingParagraph(enriched);

  return [
    `<p><strong>${escapeHtml(isim)}</strong></p>`,
    `<p>${escapeHtml(parca1)}</p>`,
    `<p>${escapeHtml(parca2)}</p>`,
    `<p>${escapeHtml(parca3)}</p>`,
    productionBlock,
    `<p><strong>Teknik özellikler</strong></p>`,
    specBlock,
    `<p><strong>Ürün özellikleri</strong></p>`,
    highlightsHtml,
    `<p><strong>Gönderim ve garanti</strong></p>`,
    shippingHtml,
    `<p>${escapeHtml(closing)}</p>`
  ]
    .filter(Boolean)
    .join('');
}

function buildSeoTitle(ctx) {
  const { isim, ayarShort, boyut, isTwentyTwoAyarBilezik } = ctx;
  const suffix = SEO_TITLE_SUFFIX;
  const maxCore = SEO_TITLE_MAX - suffix.length;
  const tw = typeWordFromKind(ctx.kind);

  let feature = '';
  if (isTwentyTwoAyarBilezik) {
    feature = '22 ayar işçiliksiz bilezik';
  } else if (boyut && String(boyut).trim()) {
    feature = String(boyut).trim();
  } else {
    feature = `${ayarShort} altın ${tw}`;
  }

  let core = `${isim} ${feature}`.replace(/\s+/g, ' ').trim();
  if (core.length > maxCore) {
    const featWithSpace = feature ? ` ${feature}` : '';
    const nameBudget = maxCore - featWithSpace.length - 1;
    if (nameBudget < 8) {
      core = `${isim.slice(0, Math.max(8, maxCore - 1)).trim()}…`;
    } else {
      let namePart = isim;
      if (namePart.length > nameBudget) {
        namePart = namePart.slice(0, nameBudget - 1).trim();
        const ls = namePart.lastIndexOf(' ');
        if (ls > 6) namePart = namePart.slice(0, ls);
        namePart = `${namePart}…`;
      }
      core = `${namePart}${featWithSpace}`.replace(/\s+/g, ' ').trim();
    }
  }
  if (core.length > maxCore) {
    core = core.slice(0, maxCore - 1).trim() + '…';
  }
  return `${core}${suffix}`;
}

function buildSeoDescription(ctx) {
  const { kisaAyar, kind, isim, isTwentyTwoAyarBilezik } = ctx;
  const tw = typeWordFromKind(kind);

  if (isTwentyTwoAyarBilezik) {
    const text =
      '22 ayar işçiliksiz bilezik modelleriyle yatırım ve şıklığı bir arada keşfedin. UKBA GOLD güvencesiyle online inceleyin.';
    return finalizeSeoDescription(text);
  }

  const variants = [
    `${kisaAyar} altın ${tw} modelleriyle stilinize zarif bir dokunuş katın; hediye veya kişisel kullanım için uygun seçenekleri UKBA GOLD güvencesiyle online inceleyin.`,
    `${kisaAyar} altın ${tw} tasarımlarıyla şıklığınızı tamamlayın. Güvenilir teslimat ve hediye paketi düşüncesiyle UKBA GOLD ile online keşfedin.`,
    `Özenli ${kisaAyar} altın ${tw} seçenekleri UKBA GOLD ile online satışta. Karşılaştırın, detayları inceleyin ve güvenle sipariş verin.`
  ];
  const idx = variantBucket(`${isim}|seod`, variants.length);
  return finalizeSeoDescription(variants[idx]);
}

/** Gerçek arama niyetine yakın, en fazla SEO_KEYWORD_MAX etiket */
function buildSeoKeywords(ctx) {
  const {
    isim,
    ayarShort,
    kind,
    kategori,
    zincir,
    tas,
    renk,
    boyut,
    isTwentyTwoAyarBilezik
  } = ctx;

  const tags = [];
  const tw = typeWordFromKind(kind);
  const kat = String(kategori || '').toLocaleLowerCase('tr-TR').trim();

  uniquePush(tags, 'ukba gold');
  uniquePush(tags, `${ayarShort} altın ${tw}`);
  uniquePush(tags, `altın ${tw}`);
  uniquePush(tags, `kadın altın ${tw}`);

  if (isTwentyTwoAyarBilezik) {
    uniquePush(tags, '22 ayar bilezik');
    uniquePush(tags, 'işçiliksiz bilezik');
    uniquePush(tags, '22 ayar işçiliksiz bilezik');
    uniquePush(tags, 'altın bilezik yatırım');
    uniquePush(tags, 'altın bilezik birikim');
  }

  if (kind === 'kolye') {
    uniquePush(tags, `${ayarShort} altın kolye`);
    if (/kolye\s*ucu/i.test(kat) || /kolye\s*ucu/i.test(isim.toLocaleLowerCase('tr-TR'))) {
      uniquePush(tags, `${ayarShort} kolye ucu`);
      uniquePush(tags, 'altın kolye ucu');
    }
    if (zincir && String(zincir).trim()) {
      uniquePush(tags, `${ayarShort} zincir kolye`);
    }
  }

  if (kind === 'bilezik' && !isTwentyTwoAyarBilezik) {
    uniquePush(tags, `${ayarShort} bilezik`);
    uniquePush(tags, 'altın bilezik');
  }

  if (kind === 'kupe') {
    uniquePush(tags, `${ayarShort} küpe`);
    uniquePush(tags, 'altın küpe');
  }

  if (kind === 'yuzuk') {
    uniquePush(tags, `${ayarShort} yüzük`);
    uniquePush(tags, 'altın yüzük');
    if (/alyans/i.test(isim + kategori)) uniquePush(tags, `${ayarShort} alyans`);
  }

  if (kind === 'diger') {
    uniquePush(tags, `${ayarShort} altın takı`);
  }

  const stop = new Set(
    'ayar altın takı ve ile için the'.split(' ').map((x) => x.toLocaleLowerCase('tr-TR'))
  );
  const nameTokens = isim
    .split(/\s+/)
    .map((w) => w.replace(/[()]/g, '').trim())
    .filter((w) => w.length > 2 && !/^\d/.test(w) && !stop.has(w.toLocaleLowerCase('tr-TR')));

  for (const token of nameTokens.slice(0, 2)) {
    const t = token.toLocaleLowerCase('tr-TR');
    uniquePush(tags, `altın ${t} ${tw}`);
  }

  if (kat && kat.length > 2 && kat.length < 48) uniquePush(tags, `${ayarShort} ${kat}`);

  if (tas && String(tas).trim() && !isStonelessTas(tas)) {
    uniquePush(tags, `${tas.toLocaleLowerCase('tr-TR')} altın ${tw}`);
  }

  if (boyut && String(boyut).trim()) {
    uniquePush(tags, `${ayarShort} ${tw} ${boyut.toLocaleLowerCase('tr-TR')} boy`);
  }

  if (renk && String(renk).trim()) {
    uniquePush(tags, `${renk.toLocaleLowerCase('tr-TR')} altın ${tw}`);
  }

  uniquePush(tags, 'online altın takı');
  uniquePush(tags, 'altın takı satın al');

  return tags
    .filter(Boolean)
    .slice(0, SEO_KEYWORD_MAX)
    .join(', ');
}

export function buildAutoProductContent(form) {
  const isim = String(form.isim || '').trim();
  const urunTipi = String(form.urunTipi || '').trim();
  const ayarMatch = urunTipi.match(/(\d{1,2})\s*Ayar/i);
  const ayar = ayarMatch ? `${ayarMatch[1]} Ayar` : 'Altın';
  const ayarShort = ayarMatch ? `${ayarMatch[1]} ayar` : 'altın';
  const milyemByAyar = { '22': '916K', '21': '875K', '18': '750K', '14': '585K', '8': '333K' };
  const milyemK = ayarMatch ? (milyemByAyar[ayarMatch[1]] || null) : null;

  const gramNum = Number.parseFloat(form.gram);
  const gramText = Number.isFinite(gramNum) ? gramNum.toFixed(2) : null;
  const zincir = String(form.zincirUzunlugu || '').trim();
  const ebat = String(form.urunCapi || '').trim();
  const tas = String(form.tasOzelligi || '').trim();
  const renk = String(form.urunRengi || '').trim();
  const kategori = String(form.urunKategorisi || '').trim();
  const model = kategori || (isim.split(' ').slice(-2).join(' ') || 'şık tasarım');
  const boyutMatch = isim.match(/\(([A-Za-zÇĞİÖŞÜçğıöşü0-9]+)\)/);
  const boyut = boyutMatch ? boyutMatch[1].toUpperCase() : '';
  const anaModel = isim
    .replace(/\([^)]+\)/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  const ebatLabel = /kolye/i.test(isim) ? 'Kolye Ucu Ebatı' : 'Ürün Ebatı';
  const titleModel = toTitleCaseTR(anaModel || isim);
  const kisaAyar = ayar.toLocaleLowerCase('tr-TR');
  const kind = resolveProductKind(isim, urunTipi, kategori);
  const isTwentyTwoAyarBilezikFlag = isTwentyTwoAyarBilezik({
    isim,
    urunTipi,
    kategori,
    kind,
    ayar
  });

  const slug = buildSlug({ isim, zincir });

  const isimShort = isim.length > 42 ? `${isim.slice(0, 39).trim()}…` : isim;

  const ctx = {
    isim,
    urunTipi,
    ayar,
    ayarShort,
    milyemK,
    gramText,
    zincir,
    ebat,
    tas,
    renk,
    kategori,
    model,
    boyut,
    anaModel,
    ebatLabel,
    titleModel,
    kisaAyar,
    kind,
    isimShort,
    isTwentyTwoAyarBilezik: isTwentyTwoAyarBilezikFlag
  };

  const description = buildDescription(ctx);
  const seoTitle = buildSeoTitle(ctx);
  const seoDescription = buildSeoDescription(ctx);
  const seoKeywords = buildSeoKeywords(ctx);

  return {
    description,
    slug,
    seoTitle,
    seoDescription,
    canonical: slug,
    seoKeywords
  };
}
